import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

const Filled = ({ url, id, onDelete, busy, tid }) => (
  <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-[#F5F5F7]" data-testid={`${tid}-photo-${id}`}>
    <img src={`${BASE}${url}`} alt="Foto della tua attività" className="h-full w-full object-cover" />
    <button type="button" onClick={() => onDelete(id)} disabled={busy} aria-label="Elimina foto"
      className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-[#111111] transition-colors hover:bg-white" data-testid={`${tid}-photo-delete-${id}`}>
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

const Empty = ({ n, onClick, busy, tid }) => (
  <button type="button" onClick={onClick} disabled={busy}
    className="grid aspect-[3/4] place-items-center rounded-2xl border-2 border-dashed border-[#d4d4d8] bg-white text-[#52525b] transition-colors hover:border-[#0066FF] hover:text-[#0066FF]" data-testid={`${tid}-photo-add-${n}`}>
    {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <span className="flex flex-col items-center gap-1.5 text-xs font-semibold"><ImagePlus className="h-6 w-6" /> Foto {n}</span>}
  </button>
);

/** 3 upload boxes backed by the account brand kit: the photos are used in every new video. */
export const BrandPhotos = ({ tid = "brand" }) => {
  const { user, login } = useAuth();
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    api.get("/account/brand").then((r) => { if (alive) setBrand(r?.data ?? null); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setBrand(data ?? null);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const pick = () => (user ? input.current?.click() : login(window.location.pathname));

  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post("/account/brand/foto", fd), "Foto caricata");
  };

  const ids = Array.isArray(brand?.foto_ids) ? brand.foto_ids : [];
  const urls = Array.isArray(brand?.foto) ? brand.foto : [];
  const max = brand?.max_foto ?? 3;
  const empties = Array.from({ length: Math.max(0, max - ids.length) }, (_, i) => ids.length + i + 1);

  return (
    <div data-testid={`${tid}-photos`}>
      <div className="grid grid-cols-3 gap-3">
        {ids.map((id, i) => <Filled key={id} id={id} url={urls[i]} busy={busy} tid={tid} onDelete={(fid) => run(() => api.delete(`/account/brand/foto/${fid}`), "Foto eliminata")} />)}
        {empties.map((n) => <Empty key={n} n={n} onClick={pick} busy={busy} tid={tid} />)}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`${tid}-photo-input`} />
    </div>
  );
};
